import { Injectable } from "@angular/core";
import { IEmittedVariable, IStaticVariable, IVariableRule } from "./variable-models";


@Injectable()
export class VariableRuleService {
    public isMatch(rule: IVariableRule, variable: IEmittedVariable): boolean {
        // If this display rule doesn't match the variable name, return false.
        if (!variable || rule.variableName !== variable.name) return false;

        // TODO: later need to consider value type in comparison. For this phase we take the basic version.
        // Check if the display rule and the variable emitted match, based on display rule operator.
        switch (rule.operator) {
            case "equals":
                return String(variable.value) === String(rule.operand);
            case "notEquals":
                return String(variable.value) !== String(rule.operand);
            case "more":
                return (variable.value as number) > (rule.operand as number);
            case "moreOrEqual":
                return (variable.value as number) >= (rule.operand as number);
            case "less":
                return (variable.value as number) < (rule.operand as number);
            case "lessOrEqual":
                return (variable.value as number) <= (rule.operand as number);
            case "between":
                return (variable.value as number) >= (rule.operand as number)
                    && (variable.value as number) <= (rule.operand as number);
            case "contains":
                return variable.value.includes(rule.operand);
            case "notContains":
                return !variable.value.includes(rule.operand);
            case "startsWith":
                return variable.value.startsWith(rule.operand);
            case "endsWith":
                return variable.value.endsWith(rule.operand);
            case "isSet":
                return !variable.empty;
            default:
                return false;
        }
    }

    public toEmittedVariables(variables: IStaticVariable[]): IEmittedVariable[] {
        if (!variables?.length) return [];

        return variables.map(variable =>
            <IEmittedVariable>{ ...variable, behaviorOnEmpty: "populate", empty: false }
        );
    }

    public containsMatch(rule: IVariableRule, variables: IEmittedVariable[]): boolean {
        return this.isMatch(rule, variables.find(variable => rule.variableName === variable.name))
    }

    public equalVariables(first: IEmittedVariable, second: IEmittedVariable): boolean {
        return first.name === second.name
            && first.value === second.value
            && first.$type === second.$type
            && first.empty === second.empty
            && first.behaviorOnEmpty === second.behaviorOnEmpty;
    }

    public isContained(variable: IEmittedVariable, variables: IEmittedVariable[]): boolean {
        return variables.some(item => this.equalVariables(variable, item));
    }

    public equalVariableSets(first: IEmittedVariable[], second: IEmittedVariable[]): boolean {
        return first.every(variable => this.isContained(variable, second))
            && second.every(variable => this.isContained(variable, first));
    }

    public filterVariableSet(names: string[], variables: IEmittedVariable[]): IEmittedVariable[] {
        if (!names?.length) return variables;
        return variables?.filter(variable => names.some(name => name === variable.name));
    }

    public embedVariableValues(source: string, variables: (IEmittedVariable | IStaticVariable)[]): string {
        let output: string = source;

        variables?.forEach(variable => {
            const value: string = this.getVariableStringValue(variable);
            const pattern = new RegExp(`(?<=\\W|^)#${variable.name}(?=\\W|$)`, "g");
            output = output.replace(pattern, value);
        });

        return output;
    }

    public cleanupUnmatchedVariables(source: string): string {
        return source.replace(/(?<=\W|^)#[0-9a-zA-Z-_]+(?=\W|$)/g, "");
    }

    public getVariableStringValue(variable: IEmittedVariable | IStaticVariable): string {
        if (!variable?.$type) return null;

        const value: string = variable.$type.endsWith("Array")
            ? this.getArrayStringValue(variable.value, variable.$type)
            : this.getSingleStringValue(variable.value, variable.$type);

        return value;
    }

    private getArrayStringValue(rawValue: any[], arrayType: string): any {
        if (rawValue == null) return null;

        const type: string = arrayType.replace(/Array$/, "");
        const values: string[] = rawValue
            .map(value => this.getSingleStringValue(value, type))
            .filter(value => value !== "");

        return values.length > 0 ? `(${values.join(", ")})` : null;
    }

    private getSingleStringValue(rawValue: any, type: string): string {
        if (rawValue == null) return "";

        switch (type) {
            case "string":
                const string: string = String(rawValue);
                return string.replace("'", "\"");
            case "number":
                const number: number = Number(rawValue);
                return number.toString();
            case "boolean":
                const boolean: boolean = Boolean(rawValue);
                return boolean ? "true" : "false";
            case "date":
                const date: Date = new Date(rawValue);
                return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
            default:
                return "";
        }
    }
}
